import { useBooleanState } from "../../../common/hooks/useBooleanState";
import { type MutableRefObject } from "react";
import { IconType } from "../../../common/components/icon/IconType";
import { SimpleMenu, useFullwidthPopoverStyles } from "../../../common/components/Menu/Menu";
import { MENU_POS_BELOW } from "../../../common/components/Menu/MenuPositions";
import { StaticMenuItem } from "../../../common/components/Menu/MenuItem";
import { canSeeSalesFinder } from "../../../Header/components/headerMenu/DesktopPersonalNav";
import { Card } from "../../../Configurators/Linear/components/Card";
import { useDispatch, useSelector } from "react-redux";
import { selectWishlistAmount } from "../../../Wishlist/redux/wishlistSelectors";
import { MobileLanguageSelector } from "../../../Header/components/LanguageSelector/LanguageSelector";
import { styled } from "@mui/material";
import { MobileNavigationList, NavigationOverview } from "./NavigationList";
import { type NavigationNode } from "../../../Header/types";
import {
  isNavigationLoading,
  selectMobileVisibleParentNodes,
  selectNavigationDisplayMode,
} from "../../../Header/redux/navigationSelectors";
import CircleSpinner from "../../../common/components/CircleSpinner/CircleSpinner";
import { closeNavigationAction } from "../../../Header/redux/actions";
import { MobileNavButton } from "./MobileNavButton";
import { GoBackLink } from "./GoBackLink";
import { STORE_FINDER } from "../../../utils/url/staticUrls";
import { getWishlistNavigationEntryUrl } from "../../../Wishlist/wishlistHelper";
import { NavigationSeriesView } from "./NavigationSeriesView";
import { useTranslation } from "react-i18next";
import { canSeeWishlistButton } from "~/utils/features";
import { SearchDisplayMode } from "~/Search/models";
import { PersonalNavItem } from "~/Header/components/headerMenu/PersonalNavItem";

const COMPARISON_LIST_URL = "/product-comparison";

interface MobileNavigationProps {
  menuAnchorRef?: MutableRefObject<HTMLElement | null>;
}

/**
 * Contains:
 * * Hamburger button to open category navigation
 * * Category navigation
 * * Extra navigation elements (wishlist, sales partner finder)
 * * Language selector
 */
export const MobileCategoryNavigation = ({ menuAnchorRef }: MobileNavigationProps) => {
  const { val: isOpen, setFalse: close, toggle } = useBooleanState(false);

  const isLoading = useSelector(isNavigationLoading);
  const visibleNodes = useSelector(selectMobileVisibleParentNodes);

  const dispatch = useDispatch();
  const popoverClasses = useFullwidthPopoverStyles();

  const highestSelectedColumn = getHighestSelectedLevel(visibleNodes);

  const transformVal = highestSelectedColumn > 1 ? `translateX(-${(highestSelectedColumn - 1) * 100}%)` : undefined;
  const getShowBack = (index: number, highestVisibleColumn: number) => index > 0 && index === highestVisibleColumn - 1;

  const displayMode = useSelector(selectNavigationDisplayMode);

  return (
    <>
      {isLoading && <CircleSpinner className={"my-auto"} />}
      {!isLoading && <MobileNavButton onClick={toggle} icon={isOpen ? IconType.Close : IconType.List} />}
      <SimpleMenu
        open={isOpen}
        onClose={close}
        TransitionProps={{
          onExited: () => dispatch(closeNavigationAction()),
        }}
        anchorEl={menuAnchorRef?.current ?? null}
        PopoverClasses={popoverClasses}
        {...MENU_POS_BELOW}
      >
        {displayMode === SearchDisplayMode.LIST && (
          <div
            className={"scha__mobile-category-navigation__container"}
            style={{ transition: "transform 600ms", transform: transformVal }}
          >
            {visibleNodes.map(({ curr, prev }, index) => (
              <div key={index} className={"scha__mobile-category-navigation__column"}>
                {[
                  curr && (
                    <NavigationColumn
                      key={curr.id}
                      parentNode={curr}
                      active={true}
                      showBack={getShowBack(index, highestSelectedColumn)}
                    />
                  ),
                  prev && prev !== curr && (
                    <NavigationColumn
                      key={prev.id}
                      parentNode={prev}
                      active={false}
                      showBack={getShowBack(index, highestSelectedColumn)}
                    />
                  ),
                ]}
              </div>
            ))}
          </div>
        )}
        {displayMode === SearchDisplayMode.SERIES_TREE && (
          <>
            <NavigationOverview node={visibleNodes[0].curr!} navigationLevel={visibleNodes[0].curr!.level} />
            <NavigationSeriesView className={"scha__nav__series-table"} />
          </>
        )}
      </SimpleMenu>
    </>
  );
};

const FADE_IN = "scha__fade-in-out__element scha__fade-in-out__fade-in";
const FADE_OUT = "scha__fade-in-out__element scha__fade-in-out__fade-out--abs-position";

interface NavigationColumnProps {
  parentNode: NavigationNode;
  showBack: boolean;
  active: boolean;
}

const NavigationColumn = ({ parentNode, active, showBack }: NavigationColumnProps) => {
  let fadeCls;
  if (active) {
    fadeCls = FADE_IN;
  } else {
    fadeCls = FADE_OUT;
  }

  const nodeLevel = parentNode.level;

  let header;
  if (nodeLevel !== -1) {
    header = (
      <>
        <StaticMenuItem className={showBack ? undefined : "v-hidden"}>
          <GoBackLink visibleColAmount={1} />
        </StaticMenuItem>
        <NavigationOverview node={parentNode} navigationLevel={parentNode.level} />
      </>
    );
  }

  return (
    <div className={fadeCls}>
      {header}
      <MobileNavigationList parentNode={parentNode} />
      {nodeLevel === -1 && <MobileLanguageSelector />}
    </div>
  );
};

interface MenuEntry {
  visibilityCheck: () => boolean;
  jsx: () => JSX.Element;
}

export const TileNavigationOverview = () => {
  const { t } = useTranslation();
  const wishlistAmount = useSelector(selectWishlistAmount);
  const { currentSize } = window.SCHA.productComparisonList;

  const entries: MenuEntry[] = [
    {
      visibilityCheck: () => canSeeSalesFinder(),
      jsx: () => (
        <a href={STORE_FINDER} data-test-id={"header-sales-partner"}>
          <Card className={"scha__mobile-navigation__tile-item"}>
            <PersonalNavItem
              data-test-id={"header-btn-sales-partner"}
              icon={IconType.Location}
              name={t("salesPartner.title")}
            />
          </Card>
        </a>
      ),
    },
    {
      visibilityCheck: () => canSeeWishlistButton(),
      jsx: () => (
        <a href={WISHLIST_URL} id="home_wishlist_btn" data-test-id={"header-wishlist"}>
          <Card className={"scha__mobile-navigation__tile-item"}>
            <PersonalNavItem
              name={t("wishlistPage.title")}
              icon={IconType.Bookmark}
              data-test-id={"header-btn-wishlist"}
              badgeContent={wishlistAmount !== undefined ? wishlistAmount : ""}
            />
          </Card>
        </a>
      ),
    },
    {
      visibilityCheck: () => true,
      jsx: () => (
        <a href={COMPARISON_LIST_URL} id="home_comparison_list_btn" data-test-id={"header-wishlist"}>
          <Card className={"scha__mobile-navigation__tile-item"}>
            <PersonalNavItem
              icon={IconType.Checklist}
              badgeContent={currentSize}
              name={t("header.link.comparison.list")}
            />
          </Card>
        </a>
      ),
    },
  ];

  const visibleMenuEntries = entries.filter((item) => item.visibilityCheck());

  return (
    <TileContainer entries={visibleMenuEntries.length}>{visibleMenuEntries.map((item) => item.jsx())}</TileContainer>
  );
};

interface ITitleContainer {
  entries: number;
}

const TileContainer = styled(StaticMenuItem)(({ entries }: ITitleContainer) => ({
  display: "flex",
  overflowX: "scroll",
  width: "100vw",
  flexWrap: "nowrap",
  margin: "0 -6px",
  "& > *": {
    margin: "0 6px",
    flex: entries > 2 ? "0 0 113px" : "0 1 50%",
  },
}));

const WISHLIST_URL = getWishlistNavigationEntryUrl();

const getHighestSelectedLevel = (visibleNodes: { curr?: unknown; prev?: unknown }[]) => {
  for (let i = 0; ; i++) {
    if (!visibleNodes[i] || !visibleNodes[i].curr) {
      return i;
    }
  }
};
